import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, Output, PLATFORM_ID, OnInit, inject } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatHint, MatInputModule } from '@angular/material/input';
import { SignatureComponent } from '../../../../../../shared/general/components/signature/signature';
import { DocumentPreview } from '../../../../../../shared/general/components/document-preview/document-preview';
import { MatProgressSpinner, MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { SuccessError } from '../../../../../../shared/general/components/success-error/success-error';
import { MatIcon } from '@angular/material/icon';
import { State, US_STATES } from '../../../../../../shared/general/models/us-states.model';
import { Property, UserProperty } from '../../../../../property/models/property.model';
import { catchError, distinctUntilChanged, filter, forkJoin, map, Observable, of, switchMap, take, tap, timer } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { User } from '../../../../../../shared/user/models/user.model';
import { SalesService } from '../../../../services/sales-agent.service';
import { DocumentService } from '../../../../../../shared/general/services/document.service';
import { AuthService } from '../../../../../../core/services/auth.service';
import { DialogService } from '../../../../../../shared/services/dialog.service';
import { PropertyService } from '../../../../../property/services/property.service';
import { SalesAgent } from '../../../../models/sales-agent.model';

@Component({
  selector: 'app-listing-form',
  imports: [
    CommonModule,
    MatCardModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatButtonModule,
    SignatureComponent,
    DocumentPreview,
    MatProgressSpinner,
    MatSelectModule,
    MatProgressSpinnerModule,
    SuccessError,
    MatHint,
    MatIcon
  ],
  templateUrl: './listing-form.html',
  styleUrl: './listing-form.scss',
})
export class ListingForm implements OnInit {
  private fb = inject(FormBuilder);
  private salesService = inject(SalesService);
  private documentService = inject(DocumentService);
  private auth = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(DialogService);
  private propertyService = inject(PropertyService);
  private destroyRef = inject(DestroyRef);
  private platformId = inject(PLATFORM_ID);

  @Output() formValidity = new EventEmitter<boolean>();
  @Output() formDataChange = new EventEmitter<any>();
  
  propertyForm!: FormGroup;
  loading = false;
  submitted = false;
  processingForm = false;
  states: State[] = US_STATES;
  userProperties: Property[] = [];
  pdfUrlForPreview: string | ArrayBuffer | Uint8Array | null = "/assets/files/Sales_Consultant_Listing_Form.pdf";
  selectedPropertyPreview: any = null;  
  user!: User;

  ngOnInit() {
    this.initForm();
    this.setupFormListener();
    this.loadData();

    this.propertyForm.statusChanges.pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(status => {
      const isValid = status === 'VALID';
      console.log(
        '[FORM STATUS] →', status,
        '| overall valid =', isValid,
        '| form.errors =', this.propertyForm.errors,
        '| consultant group status =', this.propertyForm.get('consultant')?.status,
        '| consultant.id status =', this.propertyForm.get('consultant.id')?.status,
        '| consultant.id pending =', this.propertyForm.get('consultant.id')?.pending,
        '| consultant.id errors =', this.propertyForm.get('consultant.id')?.errors,
        '| host.signatureImage value =', !!this.propertyForm.get('host.signatureImage')?.value,
        '| all touched =', this.propertyForm.touched
      );
      
      this.propertyForm.statusChanges.pipe(
        filter(status => status === 'VALID'),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        console.log('[FORM MILESTONE] Form first reached VALID state!');
      });
      
      this.formValidity.emit(isValid);
      console.log('[FORM EMIT] validity sent to parent:', isValid);

      if (isValid) {
        this.formDataChange.emit(this.propertyForm.getRawValue());
      }

      this.cdr.detectChanges();
    });
  }

  initForm() {
    this.propertyForm = this.fb.group({
      address: this.fb.group({
        propertyId: ['', Validators.required],
        street: ['', Validators.required],
        city: ['', Validators.required],
        state: ['', Validators.required],
        zip: ['', [Validators.required, Validators.pattern('^[0-9]{5}$')]]
      }),
      host: this.fb.group({
        id: ['', Validators.required],
        role: ['Property Owner', Validators.required],
        name: ['', Validators.required],
        signatureImage: ['', Validators.required],
        date: [{ value: new Date().toISOString().substring(0, 10), disabled: true}, Validators.required]
      }),
      consultant: this.fb.group({
        name: ['', Validators.required],
        id: ['', Validators.required, [this.salesAgentValidator()]],
        date: [{ value: new Date().toISOString().substring(0, 10), disabled: true }, Validators.required]
      })
    });
  }

  setupFormListener() {
    this.propertyForm.get('address.propertyId')?.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(id => {
      if (!id) {
        this.selectedPropertyPreview = null;
        this.propertyForm.get('address')?.patchValue({
          street: '',
          city: '',
          state: null,
          zip: ''
        });
      } else {
        const selected = this.userProperties.find(p => p.id === id);
        if (selected) {
          this.selectedPropertyPreview = selected;
          const matchingState = this.states.find(s => 
            s.name === selected.address.state || s.code === selected.address.state
          );
          
          this.propertyForm.get('address')?.patchValue({
            street: selected.address.street,
            city: selected.address.city,
            state: matchingState ? matchingState.code : selected.address.state,
            zip: selected.address.zipCode
          });
        }
      }
      this.cdr.detectChanges();
    });
  }

  loadData() {
    this.auth.user$.pipe(
      filter(user => !!user),
      distinctUntilChanged(),
      
      tap((user: User) => {
        this.user = user;
        this.propertyForm.get('host.id')?.setValue(user.id);
      }),

      switchMap((user: User) => {
        if (user.affiliateId) {
          this.propertyForm.get('consultant.id')?.setValue(user.affiliateId);
          
          return this.salesService.getSalesAgentById(user.affiliateId.toString()).pipe(
            tap(agent => {
              this.propertyForm.get('consultant.name')?.setValue(`${agent.firstName} ${agent.lastName}`);
            }),
            map(() => user)
          );
        }
        return of(user);
      }),
      switchMap((user: User) => this.propertyService.getUserProperties(user.id)),
      switchMap((userProperties: UserProperty[]) => {
        console.log(userProperties);
        if (userProperties.length === 0) return of([]); 
        
        const requests = userProperties.map(up => 
          this.propertyService.getPropertyById(up.propertyId)
        );
        
        return forkJoin(requests);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((fullProperties: Property[]) => {
      this.userProperties = fullProperties.filter(p => p.isPublished);
      this.cdr.detectChanges();
    });

    this.documentService.loadSavedForm().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((data: any) => {
      if (data) {
        this.propertyForm.patchValue(data, { emitEvent: false });
        this.propertyForm.updateValueAndValidity({ onlySelf: false, emitEvent: true });
        this.cdr.detectChanges();
      }
    });
  }

  handleSignature(signature: string) {
    console.log('signature', signature);
    this.propertyForm.get('host.signatureImage')?.setValue(signature);
    this.propertyForm.get('host.signatureImage')?.markAsTouched();
    this.cdr.detectChanges();
    console.log(this.propertyForm.value);
  }

  private salesAgentValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = typeof control.value === 'string' 
        ? control.value.trim() 
        : control.value;

      if (!value) {
        return of(null);
      }

      console.log('[VALIDATOR] Starting check for ID:', value);

      return timer(300).pipe(
        switchMap(() => this.salesService.getSalesAgentById(value)),
        map(agent => {
          if (agent) {
            const fullName = `${agent.firstName} ${agent.lastName}`;
            
            this.propertyForm.get('consultant.name')?.setValue(fullName, { emitEvent: false });
            console.log('[VALIDATOR SUCCESS] Name set to:', fullName);

            return null;
          }
          return { agentNotFound: true };
        }),
        catchError(err => {
          console.warn('[VALIDATOR] Error fetching agent:', err);
          return of({ agentNotFound: true });
        }),
        take(1)
      );
    };
  }
  
  verifySalesAgent(id: number) {
    this.salesService.getSalesAgentById(id.toString()).subscribe((agent) => {
      console.log('agent', agent);
    });
  }
}